import React, { useRef, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { get, post } from "util/requests";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import AddSubjectMenu from "./components/add_subject_menu";
import ModifySubjectColumnsMenu from "./components/modify_subject_columns_menu";
import DownloadSubjectsMenu from "./components/download_subjects_menu";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { TextField } from "@mui/material";
import { useLocation, useSearchParams } from "react-router-dom";
import AllDomainsTable from "./components/all_domains_table";
import router from "routes";
import moment from "moment";
import DomainTag from "./components/domain_tag";
import { CheckIcon } from "ui/icons";
import Tooltip from "@mui/material/Tooltip";

import MobileSubjectList from "./components/mobile_subject_list";

import styles from "./subjects.module.css";
import strings from "strings";

const initial_column_set_all = [
  "id",
  "status",
  "site_id",
  "consent_status",
  "randomization_status",
  "edc_status",
  "sae_status",
  "screened_domains",
  "consented_domains",
  "randomization_domains",
  "queries",
];
const all_column_set_all = (permissions) => [
  {
    field: "id",
    headerName: "ID",
    width: 80,
  },
  {
    field: "site_id",
    headerName: "Site (Code)",
    width: 200,
    align: "left",
    headerAlign: "center",
    renderCell: ({ row }) => `${row.site_name} (${row.site_id})`,
  },
  {
    field: "randomization_status",
    headerName: "Randomized",
    width: 180,
    align: "center",
    headerAlign: "center",
    renderCell: ({ row }) => (
      <div className={styles.icon_wrapper}>
        {row.randomization_status === "okay" ? <CheckIcon fontSize="18px" /> : ""}
      </div>
    ),
  },
  {
    field: "screened_domains",
    headerName: "Screened Domains",
    width: 200,

    renderCell: ({ row }) => (
      <div className={styles.domain_tile_wrapper}>
        {(row.screened_domains &&
          row.screened_domains.constructor === Array &&
          row.screened_domains.map((d) => <DomainTag text={d} />)) || <div />}
      </div>
    ),
  },
  {
    field: "consented_domains",
    headerName: "Consented Domains",
    width: 200,

    renderCell: ({ row }) => (
      <div className={styles.domain_tile_wrapper}>
        {(row.consented_domains &&
          row.consented_domains.constructor === Array &&
          row.consented_domains.map((d) => <DomainTag text={d} />)) || <div />}
      </div>
    ),
  },
  {
    field: "randomization_domains",
    headerName: "Randomized Domains",
    width: 200,

    renderCell: ({ row }) => (
      <div className={styles.domain_tile_wrapper}>
        {(row.randomization_domains &&
          row.randomization_domains.constructor === Array &&
          row.randomization_domains.map((d) => <DomainTag text={d} />)) || <div />}
      </div>
    ),
  },
  {
    field: "queries",
    headerName: "Queries",
    width: 100,
    renderCell: ({ row }) => {
      if (
        (permissions.includes("start_query") ||
          permissions.includes("resolve_query") ||
          permissions.includes("respond_query") ||
          permissions.includes("view_query_status")) &&
        row.queries?.total
      ) {
        return (
          <React.Fragment>
            <Tooltip title="Open Queries" className={styles.query_margin}>
              <span className={row.queries?.open ? styles.open_query : styles.total_query}>{row.queries?.open}</span>
            </Tooltip>
            <span className={styles.divider}>|</span>
            <Tooltip title="Responded Queries" className={styles.query_margin}>
              <span className={row.queries?.responded ? styles.responded_query : styles.total_query}>
                {row.queries?.responded}
              </span>
            </Tooltip>

            <Tooltip title="Total Queries" className={styles.query_margin}>
              <span className={styles.total_query}>({row.queries?.total})</span>
            </Tooltip>
          </React.Fragment>
        );
      } else {
        return "";
      }
    },
  },
  {
    field: "first_randomized_on",
    headerName: `First Randomized On (${moment.tz(moment.tz.guess()).zoneAbbr()})`,
    width: 240,
    headerAlign: "center",
    align: "center",
    renderCell: ({ row }) => {
      return (row.first_randomized_on && moment(row.first_randomized_on).format("yyyy-MMM-DD HH:mm:ss")) || "";
    },
  },
  {
    field: "last_randomized_on",
    headerName: `Last Randomized On (${moment.tz(moment.tz.guess()).zoneAbbr()})`,
    width: 240,
    headerAlign: "center",
    align: "center",
    renderCell: ({ row }) => {
      return (row.last_randomized_on && moment(row.last_randomized_on).format("yyyy-MMM-DD HH:mm:ss")) || "";
    },
  },
  {
    field: "crf_version",
    headerName: `CRF Version`,
    width: 120,
    headerAlign: "center",
    align: "center",
  },
  {
    field: "primary_outcome_complete",
    headerName: `Primary Outcome`,
    width: 140,
    headerAlign: "center",
    align: "center",
    renderCell: ({ row }) => (
      <div className={styles.icon_wrapper}>{row.primary_outcome_complete ? <CheckIcon fontSize="18px" /> : ""}</div>
    ),
  },
  {
    field: "end_of_study_complete",
    headerName: `End of Study`,
    width: 120,
    headerAlign: "center",
    align: "center",
    renderCell: ({ row }) => (
      <div className={styles.icon_wrapper}>{row.end_of_study_complete ? <CheckIcon fontSize="18px" /> : ""}</div>
    ),
  },
];

export default function Subjects() {
  const location = useLocation();

  const permissions = useSelector((state) => state.user.userData.permissions);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSubMenuOpen, setSubMenuOpen] = useState(false);
  const [isColumnMenuOpen, setColumnMenuOpen] = useState(false);
  const [isDownloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const [searchText, setSearchText] = useState(searchParams.get("search") || "");

  const [selectedColumnsAll, setSelectedColumnsAll] = useState(initial_column_set_all);
  const [selectedDomain, setSelectedDomain] = useState("all");
  const [options, setOptions] = useState([{ code: "all", name: "All" }]);
  const [sites, setSites] = useState([{ code: "all", name: "All" }]);
  const [showSearch, setShowSearch] = useState(false);

  const subjectMenuRef = useRef();
  const columnMenuRef = useRef();
  const downloadMenuRef = useRef();

  const columns_all = all_column_set_all(permissions).filter((c) => selectedColumnsAll.includes(c.field));

  const userDomains = useSelector((state) => state.user.userData.domains);
  const userSiteIds = useSelector((state) => state.user.userData.sites);
  const active_study_id = useSelector((state) => state.study.active_study_id);

  useEffect(() => {
    post("/footprint", {
      path: location.pathname,
      action: "pageload",
      name: "subjects_table",
    });
  }, [location.pathname]);

  const handleDocumentClick = (event) => {
    if (event.target && columnMenuRef && columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
      setColumnMenuOpen(false);
    }
    if (event.target && subjectMenuRef && subjectMenuRef.current && !subjectMenuRef.current.contains(event.target)) {
      setSubMenuOpen(false);
    }
    if (event.target && downloadMenuRef && downloadMenuRef.current && !downloadMenuRef.current.contains(event.target)) {
      setDownloadMenuOpen(false);
    }
  };

  useEffect(() => {
    get(`/invite_user_sites_and_roles/${active_study_id}`).then(({ data }) => {
      let domains_available = data.domains.filter((d) => userDomains.includes(d.code));
      setOptions(options.concat(domains_available));
      let sites_available = data.sites.filter((s) => userSiteIds.includes(s.id));
      setSites(sites.concat(sites_available));

      //we need to ensure that the search bar params that are limited are not without the limited list
      let site = searchParams.get("site");
      let domain = searchParams.get("domain");

      let update_searchparams;
      if (site) {
        if (!sites_available.map((s) => s.code).includes(site)) {
          searchParams.delete("site");
          update_searchparams = true;
        }
      }
      if (domain) {
        if (!domains_available.map((d) => d.code).includes(domain)) {
          searchParams.delete("domain");
          update_searchparams = true;
        }
      }

      if (update_searchparams) {
        setSearchParams(searchParams);
      }
      if (searchParams.size) {
        setShowSearch(true);
      }
    });
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleDocumentClick);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, []);

  const setColumnShowingState = (field, is_showing) => {
    let columns = [...selectedColumnsAll];

    if (is_showing) {
      columns.push(field);
    } else {
      columns = columns.filter((c) => c !== field);
    }

    setSelectedColumnsAll(columns);
  };

  const onKeyDown = (key) => {
    if (key.keyCode === 13) {
      key.target.blur();
    }
  };

  let flag_options = [];
  if (permissions.includes("set_monitored_flag")) {
    flag_options.push({ flag: "remonitored", text: strings.re_monitor, icon: "M" });
  }
  if (permissions.includes("set_qc_flag")) {
    flag_options.push({ flag: "reqa", text: strings.re_qa, icon: "Q" });
  }
  if (permissions.includes("set_pi_signed_flag")) {
    flag_options.push({ flag: "re_site_signoff", text: strings.re_site_signoff, icon: "S" });
  }
  if (flag_options.length) {
    flag_options.unshift({ flag: "clear", text: "clear", icon: "" });
  }

  if (window.innerWidth >= 480)
    return (
      <div className={styles.flex}>
        <div className={styles.table_header_block}>
          <div className={styles.header_block_left}>
            <h3>Subjects</h3>
            {permissions.includes("randomize") ? (
              <div>
                <AddCircleOutlineIcon className={styles.table_action_icon} onClick={() => setSubMenuOpen(true)} />
                {isSubMenuOpen ? <AddSubjectMenu ref={subjectMenuRef} router={router} /> : null}
              </div>
            ) : null}
          </div>
          {showSearch && (
            <div className={styles.search_wrapper}>
              <div className={styles.search_component}>
                <TextField
                  id="search_subject"
                  placeholder="Search"
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                  }}
                  onBlur={() => {
                    if (searchText === "") {
                      searchParams.delete("search");
                    } else {
                      searchParams.set("search", searchText);
                    }
                    setSearchParams(searchParams);
                  }}
                  onKeyDown={onKeyDown}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "5px",
                      marginLeft: "10px",
                      textAlign: "left",
                      minWidth: "100px",
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon sx={{ color: "#999", cursor: "pointer" }} onClick={() => {}} />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
              <div className={styles.search_component}>
                <div className={styles.domain_drop_title}>Site</div>
                <Select
                  value={searchParams.get("site") || ""}
                  sx={{
                    fontSize: 14,
                    minWidth: 200,
                    height: 30,
                    padding: 0,
                    alignSelf: "center",
                  }}
                  onChange={(e) => {
                    // setSelectedDomain(e.target.value);
                    if (e.target.value === "all") {
                      searchParams.delete("site");
                    } else {
                      searchParams.set("site", e.target.value);
                    }
                    setSearchParams(searchParams);
                  }}
                >
                  {sites &&
                    sites.length &&
                    sites.map((o) => (
                      <MenuItem key={o.code} value={o.code}>
                        {`${o.name}${o.code !== "all" ? ` (${o.code})` : ""} `}
                      </MenuItem>
                    ))}
                </Select>
              </div>
              <div className={styles.search_component}>
                <div className={styles.domain_drop_title}>Domain</div>
                <Select
                  value={searchParams.get("domain") || ""}
                  sx={{
                    fontSize: 14,
                    minWidth: 200,
                    height: 30,
                    padding: 0,
                    alignSelf: "center",
                  }}
                  onChange={(e) => {
                    // setSelectedDomain(e.target.value);
                    if (e.target.value === "all") {
                      searchParams.delete("domain");
                    } else {
                      searchParams.set("domain", e.target.value);
                    }
                    setSearchParams(searchParams);
                  }}
                >
                  {options &&
                    options.length &&
                    options.map((o) => (
                      <MenuItem key={o.code} value={o.code}>
                        {o.name}
                      </MenuItem>
                    ))}
                </Select>
              </div>
              {flag_options.length ? (
                <div className={styles.search_component}>
                  <div className={styles.domain_drop_title}>Flag</div>
                  <Select
                    value={
                      (searchParams.get("flag") && flag_options.find((f) => f.flag === searchParams.get("flag"))) || ""
                    }
                    sx={{
                      fontSize: 14,
                      minWidth: 200,
                      height: 30,
                      padding: 0,
                      alignSelf: "center",
                    }}
                    onChange={(e) => {
                      if (e.target.value.flag === "clear") {
                        searchParams.delete("flag");
                      } else {
                        searchParams.set("flag", e.target.value.flag);
                      }
                      setSearchParams(searchParams);
                    }}
                  >
                    {flag_options.map((o) => (
                      <MenuItem key={o.code} value={o}>
                        <div>
                          <span className={styles.re_letter_icon}>{o.icon}</span>
                          <span className={o.flag !== "clear" ? styles.re_string_text : ""}>
                            {o.flag !== "clear" ? `- ` : ""}
                            {o.text}
                          </span>
                        </div>
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              ) : null}
            </div>
          )}

          <div>
            <FileDownloadIcon className={styles.table_action_icon} onClick={() => setDownloadMenuOpen(true)} />
            {isDownloadMenuOpen ? (
              <DownloadSubjectsMenu
                ref={downloadMenuRef}
                selectedDomain={selectedDomain}
                selectedColumns={selectedColumnsAll}
                all_column_set_all={all_column_set_all(permissions)}
              />
            ) : null}
            <ViewWeekIcon className={styles.table_action_icon} onClick={() => setColumnMenuOpen(true)} />
            <SearchIcon
              className={styles.table_action_icon}
              onClick={() => {
                setShowSearch(!showSearch);
                if (showSearch) {
                  setSearchParams();
                  setSearchText("");
                }
              }}
            />
            {isColumnMenuOpen ? (
              <ModifySubjectColumnsMenu
                ref={columnMenuRef}
                availableColumns={all_column_set_all(permissions)}
                selectedColumns={selectedColumnsAll}
                setColumnShowingState={setColumnShowingState}
              />
            ) : null}
          </div>
        </div>
        <div className={styles.table_wrapper}>
          <AllDomainsTable
            columns_all={columns_all}
            study_id={(active_study_id && parseInt(active_study_id)) || 1}
            selectedDomain={searchParams.get("domain") || ""}
            searchText={searchParams.get("search") || ""}
            site={searchParams.get("site") || ""}
            selectedFlag={searchParams.get("flag") || ""}
          />
        </div>
      </div>
    );
  else {
    return <MobileSubjectList study_id={active_study_id} />;
  }
}
